import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { throttle } from 'throttle-debounce';

import { trackBubbleButtonClick, trackSubsChatRichBubbleClick } from './tracking';
import { openClientInfoFromDeeplinkThunk } from '../../actions/userCard/openClientInfoFromDeeplink';
import { sendWsAnswer } from '../../actions/wsAnswer';
import { Promo, Socket } from '../../components/ActionButton';
import { CLIENT_INFO_BUBBLE_TYPE } from '../../constants/clientInfoBubbleType';
import { currentOpenChatIDSelector } from '../../selectors';
import { TBubbleActionButton, TBubbleBody } from '../../types/messageBubble';
import { DemandActionContainer } from '../DemandActionContainer';

interface IBubbleButtonContainerProps {
  messageId: string;
  button: TBubbleActionButton;
  analyticsBubbleType: string;
  bubbleTitle?: string;
  bubbleBody?: TBubbleBody[] | null;
}

const DEMAND_TRAP_LINK = 'homeownerLeadCreateMessage';

const THROTTLE_DELAY = 3000;

export const BubbleButtonContainer: React.FC<IBubbleButtonContainerProps> = props => {
  const { button, messageId, analyticsBubbleType, bubbleTitle, bubbleBody } = props;
  const { link, type, text, socketPayload, analyticsButtonName } = button;

  const dispatch = useDispatch();

  const chatId = useSelector(currentOpenChatIDSelector) as string;

  const trackRichBubbleClick = React.useCallback(() => {
    if (analyticsBubbleType !== CLIENT_INFO_BUBBLE_TYPE) {
      return;
    }

    trackSubsChatRichBubbleClick({
      analyticsBubbleType,
      title: bubbleTitle,
      body: bubbleBody ?? null,
      buttonTitle: text,
    });
  }, [analyticsBubbleType, bubbleBody, bubbleTitle, text]);

  const handleDeeplinkClick = React.useCallback(
    (evt: React.MouseEvent<HTMLAnchorElement>) => {
      // Инфо о клиенте уже доступна в текущем чате, поэтому вместо перехода по ссылке
      // (который открывал бы новое окно) открываем карточку собеседника на месте.
      if (analyticsBubbleType === CLIENT_INFO_BUBBLE_TYPE) {
        evt.preventDefault();
        dispatch(openClientInfoFromDeeplinkThunk());
      }

      trackBubbleButtonClick(chatId, analyticsBubbleType, analyticsButtonName);
      trackRichBubbleClick();
    },
    [analyticsBubbleType, analyticsButtonName, chatId, dispatch, trackRichBubbleClick],
  );

  const throttledSocketClick = React.useCallback(
    throttle(
      THROTTLE_DELAY,
      () => {
        dispatch(sendWsAnswer({ messageId, payload: socketPayload as string }));
        trackBubbleButtonClick(chatId, analyticsBubbleType, analyticsButtonName);
        trackRichBubbleClick();
      },
      { noTrailing: true },
    ),
    [analyticsBubbleType, analyticsButtonName, chatId, dispatch, messageId, socketPayload, trackRichBubbleClick],
  );

  if (link === DEMAND_TRAP_LINK) {
    return <DemandActionContainer actionButton={button} />;
  }

  if (type === 'socket') {
    return <Socket text={text} onClick={throttledSocketClick} />;
  }

  if (type === 'deeplink' && link) {
    return <Promo link={link as string} text={text} onClick={handleDeeplinkClick} />;
  }

  return null;
};
